import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Zap, ArrowRight } from "lucide-react";
import { useSalesStatus } from "./PhysicalSalesBanner";

/**
 * Hero rotatif affiché sur la home page UNIQUEMENT quand :
 *  - La vente physique est fermée
 *  - Le visiteur est en France / UE
 *
 * Objectif : rappeler que les PDFs et les applications restent 100% disponibles
 * pendant la fermeture de la rubrique matériel, pour maintenir le chiffre d'affaires.
 */
const SLIDES = [
  {
    id: "pdf",
    tag: "Ressources PDF",
    icon: FileText,
    title: "Nos cahiers cliniques téléchargeables",
    text: "Neurologie & Art, Comprendre l'implicite, Neuro Gym : plus de 40 cahiers PDF prêts à imprimer, livrés par email immédiatement après paiement.",
    cta_label: "Explorer les ressources PDF",
    cta_link: "/ressources",
    gradient: "from-pedablue via-pedablue to-ink",
  },
  {
    id: "flashread",
    tag: "Application FlashRead Kids",
    icon: Zap,
    title: "FlashRead Kids — la lecture flash pour les DYS",
    text: "14 modules d'entraînement à la lecture rapide, présentation RSVP, IA générative de listes personnalisées. Disponible sur iOS et Google Play.",
    cta_label: "Découvrir FlashRead Kids",
    cta_link: "/logiciels/flash-read",
    gradient: "from-coral-dark via-coral to-amber-500",
  },
];

export default function DigitalPushHero() {
  const { should_block_physical } = useSalesStatus();
  const [i, setI] = useState(0);

  useEffect(() => {
    if (!should_block_physical) return;
    const id = setInterval(() => setI((v) => (v + 1) % SLIDES.length), 6000);
    return () => clearInterval(id);
  }, [should_block_physical]);

  if (!should_block_physical) return null;

  const s = SLIDES[i];
  const Icon = s.icon;

  return (
    <section
      className="max-w-6xl mx-auto px-4 lg:px-8 my-8"
      data-testid="digital-push-hero"
    >
      <div className={`relative overflow-hidden rounded-3xl bg-gradient-to-br ${s.gradient} text-white shadow-2xl`}>
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.4) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.3) 0%, transparent 45%)",
        }} />
        <div className="relative p-8 md:p-10 lg:p-12 flex flex-col md:flex-row items-center gap-6 md:gap-10">
          <div className="hidden md:flex w-24 h-24 rounded-3xl bg-white/15 backdrop-blur-sm items-center justify-center flex-shrink-0">
            <Icon size={44} className="text-white" />
          </div>
          <div className="flex-1 text-center md:text-start">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-widest mb-3">
              <Icon size={12} /> {s.tag}
            </div>
            <h3 className="font-display text-2xl md:text-3xl lg:text-4xl font-light leading-tight mb-3" data-testid="digital-push-title">
              {s.title}
            </h3>
            <p className="text-sm md:text-base opacity-95 max-w-2xl leading-relaxed" data-testid="digital-push-text">
              {s.text}
            </p>
            <p className="text-[11px] opacity-70 mt-3 italic">
              💡 Les ressources numériques restent disponibles pendant la fermeture temporaire de la rubrique matériel physique.
            </p>
          </div>
          <Link
            to={s.cta_link}
            data-testid="digital-push-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-ink font-semibold hover:bg-ivory-100 transition whitespace-nowrap no-underline flex-shrink-0 shadow-lg"
          >
            {s.cta_label} <ArrowRight size={16} />
          </Link>
        </div>
        {/* Dots indicator */}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
          {SLIDES.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setI(idx)}
              data-testid={`digital-push-dot-${idx}`}
              className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 bg-white" : "w-1.5 bg-white/40 hover:bg-white/70"}`}
              aria-label={`Slide ${idx + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
